import { ref, computed } from 'vue'
import logger from '../utils/logger'
import { menusApi, type MenuItem, type Role, type CreateMenuItemRequest, type UpdateMenuItemRequest } from '../services/api'
import { useAuthStore } from '../stores/auth'

export function useMenu() {
  const authStore = useAuthStore()
  const menuItems = ref<MenuItem[]>([])
  const allMenuItems = ref<MenuItem[]>([])
  const availableRoles = ref<Role[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const currentUser = computed(() => authStore.currentUser)
  const currentRole = computed(() => currentUser.value?.role)

  // Get menu items for current user role
  const loadMenuItems = async () => {
    try {
      loading.value = true
      error.value = null
      
      logger.debug('Loading menu items for role:', currentRole.value)
      const response = await menusApi.getMenuItems(currentRole.value)
      logger.debug('Menu API response:', response.data)
      if (response.data.success) {
        menuItems.value = response.data.data || []
        logger.debug('Loaded menu items:', menuItems.value.length, 'items')
        logger.debug('First few items:', menuItems.value.slice(0, 3).map(item => item.label))
      } else {
        logger.error('Menu API returned error:', response.data)
      }
    } catch (err: any) {
      logger.error('Failed to load menu items:', err)
      error.value = 'Failed to load menu items'
    } finally {
      loading.value = false
    }
  }

  // Get all menu items for management
  const loadAllMenuItems = async () => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.getAllMenuItems()
      if (response.data.success) {
        allMenuItems.value = response.data.data || []
      }
    } catch (err: any) {
      logger.error('Failed to load all menu items:', err)
      error.value = 'Failed to load menu items'
    } finally {
      loading.value = false
    }
  }

  // Get available roles
  const loadAvailableRoles = async () => {
    try {
      const response = await menusApi.getAvailableRoles()
      if (response.data.success) {
        availableRoles.value = response.data.data || []
      }
    } catch (err: any) {
      logger.error('Failed to load roles:', err)
      error.value = 'Failed to load roles'
    }
  }

  // Create menu item
  const createMenuItem = async (data: CreateMenuItemRequest) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.createMenuItem(data)
      if (response.data.success) {
        await loadAllMenuItems() // Refresh the management list
        await loadMenuItems() // Also refresh the sidebar menu items
        return response.data.data
      }
    } catch (err: any) {
      logger.error('Failed to create menu item:', err)
      error.value = err.response?.data?.message || 'Failed to create menu item'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Update menu item
  const updateMenuItem = async (id: number, data: UpdateMenuItemRequest) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.updateMenuItem(id, data)
      if (response.data.success) {
        await loadAllMenuItems() // Refresh the management list
        await loadMenuItems() // Also refresh the sidebar menu items
        return response.data.data
      }
    } catch (err: any) {
      logger.error('Failed to update menu item:', err)
      error.value = err.response?.data?.message || 'Failed to update menu item'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Delete menu item
  const deleteMenuItem = async (id: number) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.deleteMenuItem(id)
      if (response.data.success) {
        await loadAllMenuItems() // Refresh the management list
        await loadMenuItems() // Also refresh the sidebar menu items
        return true
      }
    } catch (err: any) {
      logger.error('Failed to delete menu item:', err)
      error.value = err.response?.data?.message || 'Failed to delete menu item'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Reorder menu items
  const reorderMenuItems = async (items: { id: number; order: number }[]) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.reorderMenuItems(items)
      if (response.data.success) {
        await loadAllMenuItems() // Refresh the management list
        await loadMenuItems() // Also refresh the sidebar menu items
        return true
      }
    } catch (err: any) {
      logger.error('Failed to reorder menu items:', err)
      error.value = err.response?.data?.message || 'Failed to reorder menu items'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Check if user can access a menu item
  const canAccessMenuItem = (menuItem: MenuItem): boolean => {
    if (!currentRole.value) return false
    
    // If no role permissions are set for this menu item, allow access
    if (!menuItem.role_permissions || menuItem.role_permissions.length === 0) {
      return true
    }
    
    const permission = menuItem.role_permissions.find(
      perm => perm.role === currentRole.value && perm.can_access
    )
    
    return !!permission
  }

  // Get filtered menu items for current user
  const filteredMenuItems = computed(() => {
    return menuItems.value.filter(item => canAccessMenuItem(item))
  })

  // Get parent menu items (no parent_id)
  const parentMenuItems = computed(() => {
    return allMenuItems.value.filter(item => !item.parent_id)
  })

  // Get child menu items for a parent
  const getChildMenuItems = (parentId: number) => {
    return allMenuItems.value.filter(item => item.parent_id === parentId)
  }

  // Restore deleted menu item
  const restoreMenuItem = async (id: number) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.restoreMenuItem(id)
      if (response.data.success) {
        // Reload all menu items to reflect the change
        await loadAllMenuItems()
        return response.data
      }
    } catch (err: any) {
      logger.error('Failed to restore menu item:', err)
      error.value = 'Failed to restore menu item'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Permanently delete menu item
  const permanentDeleteMenuItem = async (id: number) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await menusApi.permanentDeleteMenuItem(id)
      if (response.data.success) {
        // Reload all menu items to reflect the change
        await loadAllMenuItems()
        return response.data
      }
    } catch (err: any) {
      logger.error('Failed to permanently delete menu item:', err)
      error.value = 'Failed to permanently delete menu item'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    menuItems,
    allMenuItems,
    availableRoles,
    loading,
    error,
    
    // Computed
    currentUser,
    currentRole,
    filteredMenuItems,
    parentMenuItems,
    
    // Methods
    loadMenuItems,
    loadAllMenuItems,
    loadAvailableRoles,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    restoreMenuItem,
    permanentDeleteMenuItem,
    reorderMenuItems,
    canAccessMenuItem,
    getChildMenuItems
  }
}
